/**
 * The reducer: newGame, legalActions, applyAction. Pure and deterministic;
 * all randomness comes from state.rngState. Implements docs/RULES.md.
 */
import { buildDeck, CARD_VALUES, deckComposition, maxPlayers, SLOTS } from './deck';
import { shuffle, toUint32 } from './rng';
import { scoreGrid, totals } from './scoring';
import type {
  Action,
  Card,
  CardValue,
  GameEvent,
  GameEventBody,
  GameState,
  HoleStack,
  NewGameOptions,
  PlayerState,
  RevealedCard,
  RuleOptions,
  Slot,
} from './types';

export const DEFAULT_RULE_OPTIONS: Readonly<RuleOptions> = Object.freeze({
  bonusRequiresAdjacent: false,
  multipleBonusGroups: true,
  deckExhaustion: 'reshuffle',
  skipOnFinalTurns: true,
  tieBreak: 'playoff',
  drawnCardVisibility: 'public',
  extendedDeck: false,
  maxReshufflesPerHole: 2,
  maxTurnsPerHole: 150,
});

/** How many events GameState.log keeps. */
export const LOG_LIMIT = 100;
export const MIN_HOLES = 1;
export const MAX_HOLES = 18;
export const DEFAULT_HOLES = 9;
export const MIN_PLAYERS = 2;

export type IllegalActionCode = 'badAction' | 'badPlayer' | 'wrongPhase' | 'notYourTurn' | 'wrongStage' | 'badSlot' | 'notAllowed';

export class IllegalActionError extends Error {
  constructor(
    message: string,
    public readonly code: IllegalActionCode = 'notAllowed',
  ) {
    super(message);
    this.name = 'IllegalActionError';
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cloneState(s: GameState): GameState {
  return {
    ...s,
    options: { ...s.options },
    players: s.players.map((p) => ({
      ...p,
      grid: p.grid.map((sl) => ({ card: sl.card, faceUp: sl.faceUp })),
      holeScores: p.holeScores.slice(),
      playoffScores: p.playoffScores.slice(),
    })),
    active: s.active.slice(),
    drawPile: s.drawPile.slice(),
    discardPile: s.discardPile.slice(),
    turnsLeftInFinal: s.turnsLeftInFinal.slice(),
    playoffPlayers: s.playoffPlayers ? s.playoffPlayers.slice() : null,
    winners: s.winners ? s.winners.slice() : null,
    log: s.log.slice(),
  };
}

function emit(s: GameState, body: GameEventBody): void {
  s.eventSeq += 1;
  s.log.push({ ...body, seq: s.eventSeq } as GameEvent);
  if (s.log.length > LOG_LIMIT) s.log.splice(0, s.log.length - LOG_LIMIT);
}

function player(s: GameState, i: number): PlayerState {
  const p = s.players[i];
  if (!p) throw new IllegalActionError(`No player ${i}`, 'badPlayer');
  return p;
}

function slotAt(p: PlayerState, i: number): Slot {
  const sl = p.grid[i];
  if (!sl) throw new IllegalActionError(`No slot ${i}`, 'badSlot');
  return sl;
}

function cardIn(sl: Slot): Card {
  if (!sl.card) throw new Error('Engine needs the authoritative state (face-down card is redacted)');
  return sl.card;
}

export function faceDownSlots(p: PlayerState): number[] {
  const out: number[] = [];
  p.grid.forEach((sl, i) => {
    if (!sl.faceUp) out.push(i);
  });
  return out;
}

export function faceUpCount(p: PlayerState): number {
  return p.grid.filter((sl) => sl.faceUp).length;
}

/** Next active player clockwise after `from`. */
function nextActive(s: GameState, from: number): number {
  const n = s.players.length;
  for (let k = 1; k <= n; k++) {
    const i = (from + k) % n;
    if (s.active.includes(i)) return i;
  }
  return from;
}

/** Active players in clockwise order starting with `start` (inclusive). */
function activeFrom(s: GameState, start: number): number[] {
  const n = s.players.length;
  const out: number[] = [];
  for (let k = 0; k < n; k++) {
    const i = (start + k) % n;
    if (s.active.includes(i)) out.push(i);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------

/** Lowest card deals; tied lowest players redraw until the tie breaks (RULES.md 5). */
function chooseDealer(s: GameState): void {
  let deck: Card[];
  [deck, s.rngState] = shuffle(buildDeck(s.options), s.rngState);
  let contenders = s.players.map((_, i) => i);
  const rounds: { player: number; card: Card }[][] = [];
  while (contenders.length > 1) {
    if (deck.length < contenders.length) {
      [deck, s.rngState] = shuffle(buildDeck(s.options), s.rngState);
    }
    const round = contenders.map((p) => ({ player: p, card: deck.pop() as Card }));
    rounds.push(round);
    const low = Math.min(...round.map((r) => r.card.value));
    contenders = round.filter((r) => r.card.value === low).map((r) => r.player);
  }
  s.dealer = contenders[0] ?? 0;
  emit(s, { type: 'dealerChosen', dealer: s.dealer, draws: rounds });
}

/** Throws unless `stack` fits these players and this deck (values exist, no value over-subscribed). */
function validateStack(stack: HoleStack, n: number, options: RuleOptions): void {
  const bad = (): never => {
    throw new Error('Invalid stack');
  };
  if (!stack || typeof stack !== 'object') bad();
  if (!Number.isInteger(stack.dealer) || stack.dealer < 0 || stack.dealer >= n) bad();
  if (!Array.isArray(stack.grids) || stack.grids.length !== n || !Array.isArray(stack.draws)) bad();
  if (stack.grids.some((g) => !Array.isArray(g) || g.length !== SLOTS)) bad();
  const all: CardValue[] = [...stack.grids.flat(), stack.discard, ...stack.draws];
  const left = deckComposition(options);
  for (const v of all) {
    if (!CARD_VALUES.includes(v)) bad();
    const k = (left.get(v) ?? 0) - 1;
    if (k < 0) bad();
    left.set(v, k);
  }
}

/**
 * The deck for a stacked deal: the stacked values come out by value (lowest
 * copy first), the rest are shuffled, and the scripted draws go on top.
 */
function stackedDeck(s: GameState, stack: HoleStack): { grids: Card[][]; discard: Card; drawPile: Card[] } {
  const pool = buildDeck(s.options);
  const take = (v: CardValue): Card => {
    const i = pool.findIndex((c) => c.value === v);
    return pool.splice(i, 1)[0] as Card;
  };
  const grids = stack.grids.map((g) => g.map(take));
  const discard = take(stack.discard);
  const draws = stack.draws.map(take);
  let rest: Card[];
  [rest, s.rngState] = shuffle(pool, s.rngState);
  return { grids, discard, drawPile: [...rest, ...draws.reverse()] };
}

/** Shuffle a full deck (or follow `stack`), deal the active players, start the discard pile. */
function dealHole(s: GameState, stack?: HoleStack): void {
  if (stack) {
    const dealt = stackedDeck(s, stack);
    s.players.forEach((p, i) => {
      p.skipsThisHole = 0;
      p.grid = (dealt.grids[i] ?? []).map((card) => ({ card, faceUp: false }));
    });
    startHole(s, dealt.drawPile, dealt.discard);
    return;
  }
  let deck: Card[];
  [deck, s.rngState] = shuffle(buildDeck(s.options), s.rngState);
  s.players.forEach((p, i) => {
    p.skipsThisHole = 0;
    p.grid = [];
    if (!s.active.includes(i)) return;
    for (let k = 0; k < SLOTS; k++) p.grid.push({ card: deck.pop() as Card, faceUp: false });
  });
  const discardTop = deck.pop() as Card;
  startHole(s, deck, discardTop);
}

function startHole(s: GameState, drawPile: Card[], discardTop: Card): void {
  s.drawPile = drawPile;
  s.discardPile = [discardTop];
  s.phase = 'initialFlip';
  s.current = nextActive(s, s.dealer);
  s.stage = 'draw';
  s.drawn = null;
  s.drawnFrom = null;
  s.wentOutBy = null;
  s.turnsLeftInFinal = [];
  s.holeTurn = 0;
  s.reshuffles = 0;
  emit(s, {
    type: 'holeStarted',
    hole: s.hole,
    playoff: s.hole > s.totalHoles,
    dealer: s.dealer,
    first: s.current,
    players: s.active.slice(),
    discardTop,
  });
}

export function newGame(opts: NewGameOptions): GameState {
  const options: RuleOptions = { ...DEFAULT_RULE_OPTIONS, ...opts.options };
  const n = opts.players.length;
  if (n < MIN_PLAYERS || n > maxPlayers(options)) {
    throw new Error(`Player count must be ${MIN_PLAYERS}-${maxPlayers(options)} (got ${n})`);
  }
  const totalHoles = opts.totalHoles ?? DEFAULT_HOLES;
  if (!Number.isInteger(totalHoles) || totalHoles < MIN_HOLES || totalHoles > MAX_HOLES) {
    throw new Error(`totalHoles must be ${MIN_HOLES}-${MAX_HOLES}`);
  }
  if (opts.stack) validateStack(opts.stack, n, options);
  const seed = toUint32(opts.seed);
  const s: GameState = {
    seed,
    rngState: seed,
    options,
    phase: 'initialFlip',
    hole: 1,
    totalHoles,
    players: opts.players.map((p) => ({
      id: p.id,
      name: p.name,
      isBot: p.isBot,
      ...(p.isBot && p.botLevel ? { botLevel: p.botLevel } : {}),
      grid: [],
      holeScores: [],
      playoffScores: [],
      skipsThisHole: 0,
    })),
    active: opts.players.map((_, i) => i),
    dealer: 0,
    current: 0,
    stage: 'draw',
    drawPile: [],
    drawPileCount: 0,
    discardPile: [],
    drawn: null,
    drawnFrom: null,
    wentOutBy: null,
    turnsLeftInFinal: [],
    holeTurn: 0,
    reshuffles: 0,
    playoffPlayers: null,
    winners: null,
    eventSeq: 0,
    log: [],
  };
  if (opts.stack) {
    s.dealer = opts.stack.dealer;
    emit(s, { type: 'dealerChosen', dealer: s.dealer, draws: [] });
  } else {
    chooseDealer(s);
  }
  dealHole(s, opts.stack);
  s.drawPileCount = s.drawPile.length;
  return s;
}

// ---------------------------------------------------------------------------
// Legal actions
// ---------------------------------------------------------------------------

function inTurnPhase(s: GameState): boolean {
  return s.phase === 'playing' || s.phase === 'finalTurns';
}

function skipAllowed(s: GameState): boolean {
  return s.phase === 'playing' || (s.phase === 'finalTurns' && s.options.skipOnFinalTurns);
}

/** Can the draw pile supply a card (directly or after a reshuffle)? */
function deckDrawable(s: GameState): boolean {
  return s.drawPileCount > 0 || s.discardPile.length > 1;
}

/**
 * Every legal action for `player` right now. Works on redacted states too
 * (uses only public information). `nextHole` is offered to human players
 * only (to all players when nobody is human) so a server driving bots never
 * skips past the hole-over scoreboard; applyAction accepts it from anyone.
 */
export function legalActions(s: GameState, playerIndex: number): Action[] {
  const p = s.players[playerIndex];
  if (!p) return [];
  const pl = playerIndex;
  switch (s.phase) {
    case 'initialFlip': {
      if (!s.active.includes(pl) || faceUpCount(p) >= 2) return [];
      return faceDownSlots(p).map((slot) => ({ type: 'flipInitial', player: pl, slot }));
    }
    case 'playing':
    case 'finalTurns': {
      if (s.current !== pl) return [];
      if (s.stage === 'draw') {
        const out: Action[] = [];
        if (deckDrawable(s)) out.push({ type: 'drawDeck', player: pl });
        if (s.discardPile.length > 0) out.push({ type: 'drawDiscard', player: pl });
        return out;
      }
      if (s.stage === 'holding') {
        const out: Action[] = [];
        for (let slot = 0; slot < SLOTS; slot++) out.push({ type: 'swap', player: pl, slot });
        if (s.drawnFrom === 'deck' && faceDownSlots(p).length > 0) out.push({ type: 'discardDrawn', player: pl });
        return out;
      }
      const down = faceDownSlots(p);
      const out: Action[] = down.map((slot) => ({ type: 'flip', player: pl, slot }));
      if (down.length === 1 && skipAllowed(s)) out.push({ type: 'skip', player: pl });
      return out;
    }
    case 'holeOver': {
      const anyHuman = s.players.some((x) => !x.isBot);
      return !anyHuman || !p.isBot ? [{ type: 'nextHole' }] : [];
    }
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Applying actions
// ---------------------------------------------------------------------------

function validate(s: GameState, a: Action): void {
  if (!a || typeof a !== 'object' || typeof (a as { type?: unknown }).type !== 'string') {
    throw new IllegalActionError('Malformed action', 'badAction');
  }
  if (a.type === 'nextHole') {
    if (s.phase !== 'holeOver') throw new IllegalActionError(`Cannot start the next hole during ${s.phase}`, 'wrongPhase');
    return;
  }
  const known = ['flipInitial', 'drawDeck', 'drawDiscard', 'swap', 'discardDrawn', 'flip', 'skip'];
  if (!known.includes(a.type)) throw new IllegalActionError(`Unknown action ${String(a.type)}`, 'badAction');
  if (!Number.isInteger(a.player) || !s.players[a.player]) throw new IllegalActionError(`No player ${String(a.player)}`, 'badPlayer');
  if ('slot' in a && (!Number.isInteger(a.slot) || a.slot < 0 || a.slot >= SLOTS)) {
    throw new IllegalActionError(`Slot must be 0-${SLOTS - 1}`, 'badSlot');
  }
  if (a.type === 'flipInitial') {
    if (s.phase !== 'initialFlip') throw new IllegalActionError('The initial flip is over', 'wrongPhase');
  } else {
    if (!inTurnPhase(s)) throw new IllegalActionError(`Cannot ${a.type} during ${s.phase}`, 'wrongPhase');
    if (s.current !== a.player) throw new IllegalActionError(`It is not player ${a.player}'s turn`, 'notYourTurn');
    const stageFor: Record<string, GameState['stage']> = {
      drawDeck: 'draw',
      drawDiscard: 'draw',
      swap: 'holding',
      discardDrawn: 'holding',
      flip: 'flip',
      skip: 'flip',
    };
    if (stageFor[a.type] !== s.stage) throw new IllegalActionError(`Cannot ${a.type} now (turn stage: ${s.stage})`, 'wrongStage');
  }
  const legal = legalActions(s, a.player);
  const ok = legal.some((l) => l.type === a.type && (!('slot' in l) || ('slot' in a && l.slot === a.slot)));
  if (!ok) throw new IllegalActionError(`Illegal ${a.type}${'slot' in a ? ` on slot ${a.slot}` : ''}`, 'notAllowed');
}

/** Pure reducer. Throws IllegalActionError for anything RULES.md does not allow. */
export function applyAction(state: GameState, a: Action): GameState {
  validate(state, a);
  const s = cloneState(state);
  switch (a.type) {
    case 'flipInitial': {
      const p = player(s, a.player);
      const sl = slotAt(p, a.slot);
      sl.faceUp = true;
      emit(s, { type: 'flippedInitial', player: a.player, slot: a.slot, card: cardIn(sl) });
      if (s.active.every((i) => faceUpCount(player(s, i)) >= 2)) {
        s.phase = 'playing';
        s.stage = 'draw';
        emit(s, { type: 'teeOffDone', first: s.current });
        beginTurn(s);
      }
      break;
    }
    case 'drawDeck': {
      if (s.drawPile.length === 0) reshuffle(s);
      const card = s.drawPile.pop() as Card;
      s.drawn = card;
      s.drawnFrom = 'deck';
      s.stage = 'holding';
      emit(s, { type: 'drewDeck', player: a.player, card });
      break;
    }
    case 'drawDiscard': {
      const card = s.discardPile.pop() as Card;
      s.drawn = card;
      s.drawnFrom = 'discard';
      s.stage = 'holding';
      emit(s, { type: 'drewDiscard', player: a.player, card });
      break;
    }
    case 'swap': {
      const p = player(s, a.player);
      const sl = slotAt(p, a.slot);
      const old = cardIn(sl);
      const oldWasFaceUp = sl.faceUp;
      const card = s.drawn as Card;
      sl.card = card;
      sl.faceUp = true;
      s.discardPile.push(old);
      s.drawn = null;
      s.drawnFrom = null;
      emit(s, { type: 'swapped', player: a.player, slot: a.slot, card, old, oldWasFaceUp });
      endTurn(s, a.player);
      break;
    }
    case 'discardDrawn': {
      const card = s.drawn as Card;
      s.discardPile.push(card);
      s.drawn = null;
      s.drawnFrom = null;
      s.stage = 'flip';
      emit(s, { type: 'discarded', player: a.player, card });
      break;
    }
    case 'flip': {
      const p = player(s, a.player);
      const sl = slotAt(p, a.slot);
      sl.faceUp = true;
      emit(s, { type: 'flipped', player: a.player, slot: a.slot, card: cardIn(sl) });
      endTurn(s, a.player);
      break;
    }
    case 'skip': {
      player(s, a.player).skipsThisHole += 1;
      emit(s, { type: 'skipped', player: a.player });
      endTurn(s, a.player);
      break;
    }
    case 'nextHole':
      startNextHole(s);
      break;
  }
  s.drawPileCount = s.drawPile.length;
  return s;
}

/** RULES.md 10: everything in the discard pile except its top card becomes the new draw pile. */
function reshuffle(s: GameState): void {
  const topCard = s.discardPile.pop();
  if (!topCard) throw new IllegalActionError('No cards left to draw', 'notAllowed');
  [s.drawPile, s.rngState] = shuffle(s.discardPile, s.rngState);
  s.discardPile = [topCard];
  s.reshuffles += 1;
  emit(s, { type: 'reshuffled', count: s.drawPile.length, discardTop: topCard });
}

/** Called after a swap, flip, or skip. */
function endTurn(s: GameState, pl: number): void {
  const p = player(s, pl);
  s.holeTurn += 1;
  s.stage = 'draw';
  if (s.phase === 'playing') {
    if (faceDownSlots(p).length === 0) {
      s.wentOutBy = pl;
      s.phase = 'finalTurns';
      s.turnsLeftInFinal = activeFrom(s, pl).slice(1);
      emit(s, { type: 'wentOut', player: pl });
      emit(s, { type: 'finalRound', reason: 'wentOut', by: pl, players: s.turnsLeftInFinal.slice() });
      if (s.turnsLeftInFinal.length === 0) return endHole(s);
      s.current = s.turnsLeftInFinal[0] as number;
      return;
    }
    s.current = nextActive(s, pl);
    beginTurn(s);
    return;
  }
  // finalTurns: this player's last shot is done.
  s.turnsLeftInFinal = s.turnsLeftInFinal.filter((i) => i !== pl);
  if (s.turnsLeftInFinal.length === 0) return endHole(s);
  s.current = s.turnsLeftInFinal[0] as number;
}

/** Start-of-turn checks during normal play: the deck-low variant and the [GUARD]. */
function beginTurn(s: GameState): void {
  if (s.phase !== 'playing') return;
  let reason: 'deckLow' | 'guard' | null = null;
  if (s.options.deckExhaustion === 'finalRound' && s.drawPile.length <= s.active.length) reason = 'deckLow';
  else if (s.reshuffles >= s.options.maxReshufflesPerHole || s.holeTurn >= s.options.maxTurnsPerHole) reason = 'guard';
  if (!reason) return;
  s.phase = 'finalTurns';
  s.wentOutBy = null;
  s.turnsLeftInFinal = activeFrom(s, s.current);
  emit(s, { type: 'finalRound', reason, by: null, players: s.turnsLeftInFinal.slice() });
}

function endHole(s: GameState): void {
  s.drawn = null;
  s.drawnFrom = null;
  s.stage = 'draw';
  s.turnsLeftInFinal = [];
  const revealed: RevealedCard[] = [];
  for (const i of s.active) {
    const p = player(s, i);
    p.grid.forEach((sl, slot) => {
      if (!sl.faceUp) {
        sl.faceUp = true;
        revealed.push({ player: i, slot, card: cardIn(sl) });
      }
    });
  }
  emit(s, { type: 'revealed', cards: revealed });

  const playoff = s.hole > s.totalHoles;
  const scores: (number | null)[] = s.players.map((p, i) =>
    s.active.includes(i) ? scoreGrid(p.grid, { options: s.options }).total : null,
  );
  s.players.forEach((p, i) => {
    const sc = scores[i] ?? null;
    if (playoff) p.playoffScores.push(sc);
    else if (sc !== null) p.holeScores.push(sc);
  });
  const tot = totals(s);
  emit(s, { type: 'holeScored', hole: s.hole, playoff, scores, totals: tot });

  let contenders: number[] | null = null;
  if (playoff) {
    const nums = s.active.map((i) => scores[i] as number);
    const low = Math.min(...nums);
    contenders = s.active.filter((i) => scores[i] === low);
  } else if (s.hole >= s.totalHoles) {
    const low = Math.min(...tot);
    contenders = tot.flatMap((t, i) => (t === low ? [i] : []));
  }

  if (contenders === null) {
    s.phase = 'holeOver';
    return;
  }
  if (contenders.length === 1 || s.options.tieBreak === 'shared') {
    s.phase = 'gameOver';
    s.winners = contenders;
    s.playoffPlayers = null;
    emit(s, { type: 'gameOver', winners: contenders.slice(), totals: tot });
    return;
  }
  s.phase = 'holeOver';
  s.playoffPlayers = contenders;
}

function startNextHole(s: GameState): void {
  const n = s.players.length;
  s.hole += 1;
  if (s.playoffPlayers) {
    s.active = s.playoffPlayers.slice().sort((a, b) => a - b);
    // Dealer = next in rotation among the tied players.
    let d = (s.dealer + 1) % n;
    while (!s.active.includes(d)) d = (d + 1) % n;
    s.dealer = d;
    emit(s, { type: 'tiebreakStarted', hole: s.hole, players: s.active.slice() });
  } else {
    s.active = s.players.map((_, i) => i);
    s.dealer = (s.dealer + 1) % n;
  }
  dealHole(s);
}

/** Replay a list of actions from newGame (handy for tests and debugging). */
export function replay(opts: NewGameOptions, actions: readonly Action[]): GameState {
  return actions.reduce((st, a) => applyAction(st, a), newGame(opts));
}
